import {Countdown} from './countdown.js';
import {escapeHtml} from '../utils/markupHelpers.js';
import {t} from '../localization/translate.js';

// Any count above one selects the plural branch in every locale's rules, unlike zero (singular in French).
const PLURAL_RULER_COUNT = 2;

export class CountdownClock {
    /**
     * @param {HTMLElement} clock
     * @param {number} endTimestamp
     * @param {() => void} onCourseStarted
     */
    constructor(clock, endTimestamp, onCourseStarted = () => {}) {
        this._clock = clock;
        this._hideZeroDays = this._clock.dataset.hideZeroDays === 'true'
            || this._clock.firstElementChild?.dataset.hideZeroDays === 'true';
        this._iterateElementObject();

        const endDate = new Date(endTimestamp * 1000);
        const duration = this._calcTimeDuration(endDate);
        if (isNaN(duration) || duration <= 0) return;

        new Countdown({
            dateEnd: endDate,
            interval: 1000,
            onTick: this._onCourseTimeTicked.bind(this),
            onEnd: onCourseStarted,
        });
    }

    _onCourseTimeTicked(properties) {
        this._update(properties);
        this._previousCountdownProps = properties;
    }

    _iterateElementObject() {
        this._numberElements = {
            days: this._clock.querySelector('.day .number'),
            hours: this._clock.querySelector('.hour .number'),
            minutes: this._clock.querySelector('.minute .number'),
            seconds: this._clock.querySelector('.second .number'),
        };

        this._unitElements = {
            days: this._clock.querySelector('.day .unit'),
            hours: this._clock.querySelector('.hour .unit'),
            minutes: this._clock.querySelector('.minute .unit'),
            seconds: this._clock.querySelector('.second .unit'),
        };
    }

    /**
     * @param {object} countdownProperties
     */
    _update(countdownProperties) {
        const {weeks, days} = countdownProperties;

        const countdownPropertiesWithoutWeek = this._convertWeekToDay(countdownProperties);

        if (!this._useDayLayout(weeks, days) && this._isDayLayout()) {
            this._renderFullCountdownLayout(countdownPropertiesWithoutWeek);
            return;
        }

        if (!this._shouldRenderDays(countdownPropertiesWithoutWeek.days)) {
            this._removeDayElement();
        }

        Object.keys(countdownPropertiesWithoutWeek).forEach((unit) => {
            this._updateCountdownElement(unit, countdownPropertiesWithoutWeek[unit]);
        });
    }

    /**
     * @param {string} property
     * @param {string} value
     */
    _updateCountdownElement(property, value) {
        if (!this._numberElements[property]) return;
        if (this._previousCountdownProps && this._previousCountdownProps[property] === value) return;

        this._numberElements[property].innerText = String(value); // eslint-disable-line unicorn/prefer-dom-node-text-content
        this._unitElements[property].innerText = this._unitLabel(property, value); // eslint-disable-line unicorn/prefer-dom-node-text-content
    }

    _renderFullCountdownLayout({days, hours, minutes, seconds}) {
        const dayHtml = this._shouldRenderDays(days)
            ? `<div class="box clock-item day flex flex-col items-center justify-center text-center">
                    <div class="fg-title-sm -mb-1 min-w-[2ch] text-center tabular-nums fg-text-neutral-default number lg:-mb-2">${days}</div>
                    ${this._renderUnit(this._unitLabel('days', PLURAL_RULER_COUNT), this._unitLabel('days', days))}
                </div>`
            : '';

        this._clock.innerHTML = `${dayHtml}
                <div class="box clock-item hour flex flex-col items-center justify-center text-center">
                    <div class="fg-title-sm -mb-1 min-w-[2ch] text-center tabular-nums fg-text-neutral-default number lg:-mb-2">${hours}</div>
                    ${this._renderUnit(this._unitLabel('hours', PLURAL_RULER_COUNT), this._unitLabel('hours', hours))}
                </div>
                <div class="box clock-item minute flex flex-col items-center justify-center text-center">
                    <div class="fg-title-sm -mb-1 min-w-[2ch] text-center tabular-nums fg-text-neutral-default number lg:-mb-2">${minutes}</div>
                    ${this._renderUnit(this._unitLabel('minutes', PLURAL_RULER_COUNT), this._unitLabel('minutes', minutes))}
                </div>
                <div class="box clock-item second flex flex-col items-center justify-center text-center">
                    <div class="fg-title-sm -mb-1 min-w-[2ch] text-center tabular-nums fg-text-neutral-default number lg:-mb-2">${seconds}</div>
                    ${this._renderUnit(this._unitLabel('seconds', PLURAL_RULER_COUNT), this._unitLabel('seconds', seconds))}
                </div>`;

        this._iterateElementObject();
    }

    /**
     * Render the unit wrapper. The invisible "ruler" span reserves the plural-form width so the
     * box does not shift when the visible text flips between singular and plural.
     * @param {string} pluralUnit Plural form, used as the invisible width ruler (e.g. "secs").
     * @param {string} visibleUnit Current form (singular or plural) shown to the user.
     * @returns {string}
     */
    _renderUnit(pluralUnit, visibleUnit) {
        return `<div class="grid">
                    <span class="col-start-1 row-start-1 invisible fg-body-sm fg-text-neutral-default" aria-hidden="true">${escapeHtml(pluralUnit)}</span>
                    <span class="unit col-start-1 row-start-1 fg-body-sm text-center fg-text-neutral-default" aria-hidden="true">${escapeHtml(visibleUnit)}</span>
                </div>`;
    }

    _removeDayElement() {
        const dayElement = this._clock.querySelector('.clock-item.day');
        if (!dayElement) return;

        const nextElement = dayElement.nextElementSibling;
        if (nextElement?.getAttribute('aria-hidden') === 'true') {
            nextElement.remove();
        }

        dayElement.remove();
        this._iterateElementObject();
    }

    _convertWeekToDay(countdownProperties) {
        const newCountdown = Object.assign({}, countdownProperties);
        newCountdown.days += newCountdown.weeks * 7;
        return newCountdown;
    }

    _useDayLayout(weeks, days) {
        return weeks >= 1 || days >= 2;
    }

    _isDayLayout() {
        return this._numberElements.days && !this._numberElements.hours;
    }

    _calcDays(duration) {
        const MILLISECONDS_IN_A_DAY = 86_400_000;
        return Math.floor(duration / MILLISECONDS_IN_A_DAY) + 1;
    }

    _calcTimeDuration(endDate) {
        return endDate.getTime() - Date.now();
    }

    _shouldRenderDays(days) {
        return !this._hideZeroDays || Number(days) > 0;
    }

    /**
     * @param {'days'|'hours'|'minutes'|'seconds'} property
     * @param {number} total
     * @returns {string}
     */
    _unitLabel(property, total) {
        switch (property) {
            case 'days':
                return t('day|days', {}, total);
            case 'hours':
                return t('hr|hrs', {}, total);
            case 'minutes':
                return t('min|mins', {}, total);
            case 'seconds':
                return t('sec|secs', {}, total);
        }

        throw new Error(`Unknown countdown unit "${property}".`);
    }
}
